import querystring from 'query-string'

import { isNil } from '../../../helpers/isNil'
import { UrlSearchParamsMapper } from '../../../hooks/useSearchParams'
import languageService from '../../../services/LanguageService'
import { VersionTagFeature } from '../../../types/VersionTag'
import { SubgenreMap } from '../../account'
import { FeatureChoiceMap } from '../../market-explorer/types/MarketExplorerSegmentConfiguration'
import { EMPTY_AGGREGATE_VALUE, FeaturesFilterValue } from '../types/ColumnFilter'
import { parseTimeSinceUpdated } from '../types/TimeSinceUpdated'
import { UpdateImpactRowFilters } from '../types/UpdateImpactRowFilters'
import { UPDATE_TAGS_NONE, UpdateImpactSearchParams } from '../types/UpdateImpactSearchParams'

/**
 * Mappers for game update impact filters to/from url search params
 */

const mapFiltersToUrlSearchParams = (filters: UpdateImpactRowFilters): UpdateImpactSearchParams => {
  const defaultSearch = querystring.parse(window.location.search)
  return {
    ...defaultSearch,
    time: filters.timeSinceUpdated,
    subgenres: Object.entries(filters.subgenres || {})
      .filter(([key, value]) => !!value)
      .map(([key, value]) => key),
    updateTags: filters.versionTags && filters.versionTags.length === 0 ? UPDATE_TAGS_NONE : filters.versionTags?.map((tag) => tag.id),
    revenue: [filters.revenue?.percentChange, filters.revenue?.valueChange],
    download: [filters.downloads?.percentChange, filters.downloads?.valueChange],
    revDL: [filters.revenueDownloadsRatio?.percentChange, filters.revenueDownloadsRatio?.valueChange],
    featureType: filters.features?.featureType,
    features: Object.entries(filters.features?.selectedFeatures || {}).map(([featureId, choiceIds]) => [featureId, ...choiceIds].join('-')),
    comparisonInterval: filters.comparisonInterval,
  }
}

const mapUrlSearchParamsToFilters = (search: UpdateImpactSearchParams): UpdateImpactRowFilters => {
  const updateTagIds = parseUpdateTagIds(search.updateTags)

  return {
    timeSinceUpdated: parseTimeSinceUpdated(search.time),
    subgenres: Array.isArray(search.subgenres)
      ? search.subgenres.reduce((acc: SubgenreMap, subgenre: string) => {
          acc[subgenre] = true

          return acc
        }, {})
      : !isNil(search.subgenres)
      ? { [search.subgenres as string]: true }
      : undefined,
    versionTags: updateTagIds
      ? languageService.getVersionTags().filter((tag) => updateTagIds.includes(tag.id))
      : languageService.getVersionTags().filter((tag) => VersionTagFeature.includes(tag.id)),
    revenue: {
      percentChange: search.revenue?.[0] || EMPTY_AGGREGATE_VALUE,
      valueChange: search.revenue?.[1] || EMPTY_AGGREGATE_VALUE,
    },
    downloads: {
      percentChange: search.download?.[0] || EMPTY_AGGREGATE_VALUE,
      valueChange: search.download?.[1] || EMPTY_AGGREGATE_VALUE,
    },
    revenueDownloadsRatio: {
      percentChange: search.revDL?.[0] || EMPTY_AGGREGATE_VALUE,
      valueChange: search.revDL?.[1] || EMPTY_AGGREGATE_VALUE,
    },
    features: {
      featureType: search.featureType || FeaturesFilterValue.Any,
      selectedFeatures: Array.isArray(search.features)
        ? search.features?.reduce((acc, featureChoices) => {
            const parts = featureChoices.split('-')
            const [featureId, ...choiceIds] = parts
            if (featureId) {
              acc[+featureId] = choiceIds.map((value) => +value)
            }
            return acc
          }, {} as FeatureChoiceMap)
        : !isNil(search.features)
        ? parseFeatures(search.features)
        : undefined,
    },
    comparisonInterval: search.comparisonInterval,
  }
}

export const mapper: UrlSearchParamsMapper<UpdateImpactSearchParams, UpdateImpactRowFilters> = {
  mapFromUrl: mapUrlSearchParamsToFilters,
  mapToUrl: mapFiltersToUrlSearchParams,
}

/**
 * Resolves the raw updateTags url parameter to tag ids. Undefined means the parameter is
 * absent (falls back to the Feature default), while an empty array means the user explicitly
 * cleared every tag via the UPDATE_TAGS_NONE sentinel. The sentinel itself is never a tag id.
 */
const parseUpdateTagIds = (updateTags?: string | string[]): string[] | undefined => {
  if (isNil(updateTags) || updateTags === '') {
    return undefined
  }

  const tagIds = Array.isArray(updateTags) ? updateTags : [updateTags as string]

  return tagIds.filter((tagId) => tagId !== UPDATE_TAGS_NONE)
}

const parseFeatures = (featureChoices: string) => {
  const parts = featureChoices.split('-')
  const [featureId, ...choiceIds] = parts

  return { [+featureId]: choiceIds.map((value) => +value) }
}
